import { useQuery, useMutation } from '@apollo/client';
import { Col, Row, Spin, Button, message, Icon, Tooltip } from 'antd';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bookingJourneyType } from 'common/constants/booking';
import { QUERY_OPPS_DETAIL, MUTATION_AUDIT_PAYOUT } from '../queries';
import { dayFormatType, formatDay } from '~helpers/day';
import { formatPriceWithSpace } from '~helpers/currency';
import { OperationType, Statuses } from '../list';
import classNames from 'classnames';
import { PayoutOppsInfo as PayoutOppsInfoIcon } from 'common/components/svgs';

const LeaseType = {
  'Long Term': 'LONG',
  'Short Term': 'SHORT',
};

const PartnerActualCommissionStatus = {
  'No Settlement': 'NO_SETTLEMENT',
  'Part Settlement': 'PART_SETTLEMENT',
  Settled: 'SETTLED',
};

const PayoutPlanStatus = {
  Unlocked: [PartnerActualCommissionStatus['No Settlement']],
  Locked: [PartnerActualCommissionStatus['Part Settlement'], PartnerActualCommissionStatus.Settled],
};

function PayoutOppsViewer({ id, bookingReferenceIds, bookingJourney, onClose }) {
  const { t } = useTranslation();

  const [lastAuditSuccessStatus, setLastAuditSuccessStatus] = useState(null);
  const [auditPayoutLoading, setAuditPayoutLoading] = useState(false);

  const { data: payoutListResponse } = useQuery(QUERY_OPPS_DETAIL, {
    variables: {
      bookingReferenceIds: bookingReferenceIds,
    },
    fetchPolicy: 'no-cache',
    onError: e => {
      message.error(e?.toString());
    },
  });

  const [auditPayout] = useMutation(MUTATION_AUDIT_PAYOUT, {
    onError: () => {
      message.error(t('sorry_an_error_occurred_please_try_again'));
    },
    refetchQueries: [QUERY_OPPS_DETAIL],
  });

  const { result, booking, partnerActualCommission, createdAt, updatedAt, contactName } =
    useMemo(() => {
      const result = payoutListResponse?.listPartnerPayoutRecords?.edges[0]?.node;

      if (bookingJourney === bookingJourneyType.MANUAL) {
        return {
          result,
          createdAt: result?.simpleOpportunity?.createdAt,
          updatedAt: result?.simpleOpportunity?.updatedAt,
          contactName: result?.simpleOpportunity?.contacts?.name,
          booking: result?.simpleOpportunity?.xbookings?.edges[0]?.node,
          partnerActualCommission: result?.simpleOpportunity?.partnerActualCommission,
        };
      } else if (bookingJourney === bookingJourneyType.SEMI_AUTOMATIC) {
        return {
          result,
          createdAt: result?.order?.createdAt,
          updatedAt: result?.order?.updatedAt,
          contactName: '-',
          booking: result?.order,
          partnerActualCommission: result?.order?.partnerActualCommission,
        };
      }
    }, [payoutListResponse]);

  const secondOpps = useMemo(() => {
    return payoutListResponse?.listPartnerPayoutRecords?.edges[1]?.node;
  }, [payoutListResponse]);

  const selectedPayout = useMemo(() => {
    return payoutListResponse?.listPartnerPayoutRecords?.edges.find(edge => edge.node.id === id)
      ?.node;
  }, [payoutListResponse]);

  const payoutStatus = useMemo(() => {
    return lastAuditSuccessStatus || selectedPayout?.status;
  }, [selectedPayout, lastAuditSuccessStatus]);

  const isAvaliableAuditPayoutStatus = useMemo(() => {
    return [Statuses.Approved, Statuses.Pending].includes(selectedPayout?.status);
  }, [selectedPayout]);

  const studentName = useMemo(() => {
    const { student } = booking ?? {};

    if (student?.passportFirstName && student?.passportLastName) {
      if (student?.passportMiddleName) {
        return `${student?.passportFirstName} ${student?.passportMiddleName} ${student?.passportLastName}`;
      } else {
        return `${student?.passportFirstName} ${student?.passportLastName}`;
      }
    }
    if (student?.firstName && student?.lastName) {
      return `${student?.firstName} ${student?.lastName}`;
    }
    return '-';
  }, [booking]);

  const handleAudit = async () => {
    try {
      setAuditPayoutLoading(true);
      const isPending = payoutStatus === Statuses.Pending;
      const auditPayoutResponse = await auditPayout({
        variables: {
          ids: [id],
          operationType: isPending ? OperationType.Approve : OperationType.Unapproved,
        },
      });
      if (auditPayoutResponse.data.bulkAuditPartnerPayoutRecords.result) {
        message.success(
          isPending
            ? t('cms.referral.payout.action.apporve.toast')
            : t('cms.referral.payout.action.unapporve.toast'),
        );
        setLastAuditSuccessStatus(isPending ? Statuses.Approved : Statuses.Pending);
        setAuditPayoutLoading(false);
      }
      if (auditPayoutResponse?.errors?.[0]?.message) {
        message.error(auditPayoutResponse.errors[0].message);
      }
    } catch (error) {
      setAuditPayoutLoading(false);
      message.error(error.toString());
    }
  };

  if (!booking) {
    return (
      <div className="payout-opps-viewer__spin">
        <Spin />
      </div>
    );
  }

  return (
    <>
      <section className="payout-opps-viewer__section">
        <h3 className="payout-opps-viewer__body__title">
          {t('cms.referral.payout.opps.modal.basic_info.title')}
        </h3>
        <Row gutter={24}>
          <Col span={12}>
            <div className="payout-opps-viewer__body__info-item">
              <span className="payout-opps-viewer__body__info-item__label">
                {`${t('cms.referral.payout.opps.modal.booking_type.label')}: `}
              </span>
              <span className="payout-opps-viewer__body__info-item__value">
                {t(
                  `cms.referral.payout.opps.modal.booking_type.${
                    bookingJourney === bookingJourneyType.MANUAL ? 'opps' : 'order'
                  }`,
                )}
              </span>
            </div>
          </Col>
          <Col span={12}>
            <div className="payout-opps-viewer__body__info-item">
              <span className="payout-opps-viewer__body__info-item__label">
                {`${t('cms.referral.payout.opps.modal.student_name.label')}: `}
              </span>
              <span className="payout-opps-viewer__body__info-item__value">{studentName}</span>
            </div>
          </Col>
          <Col span={12}>
            <div className="payout-opps-viewer__body__info-item">
              <span className="payout-opps-viewer__body__info-item__label">
                {`${t('cms.referral.payout.opps.modal.city.label')}: `}
              </span>
              <span className="payout-opps-viewer__body__info-item__value">
                {`${booking?.city?.name}, ${booking?.city?.country?.name}`}
              </span>
            </div>
          </Col>
          <Col span={12}>
            <div className="payout-opps-viewer__body__info-item">
              <span className="payout-opps-viewer__body__info-item__label">
                {`${t('cms.referral.payout.opps.modal.student_email.label')}: `}
              </span>
              <span className="payout-opps-viewer__body__info-item__value">
                {`${booking?.student?.emailAddress}`}
              </span>
            </div>
          </Col>
          <Col span={12}>
            <div className="payout-opps-viewer__body__info-item">
              <span className="payout-opps-viewer__body__info-item__label">
                {`${t('cms.referral.payout.opps.modal.university.label')}: `}
              </span>
              <span className="payout-opps-viewer__body__info-item__value">
                {booking?.student?.currentUniversity || '-'}
              </span>
            </div>
          </Col>
          <Col span={12}>
            <div className="payout-opps-viewer__body__info-item">
              <span className="payout-opps-viewer__body__info-item__label">
                {`${t('cms.referral.payout.opps.modal.student_phone_number.label')}: `}
              </span>
              <span className="payout-opps-viewer__body__info-item__value">
                {booking?.student?.phoneNumber}
              </span>
            </div>
          </Col>
          <Col span={12}>
            <div className="payout-opps-viewer__body__info-item">
              <span className="payout-opps-viewer__body__info-item__label">
                {`${t('cms.referral.payout.opps.modal.create_time.label')}: `}
              </span>
              <span className="payout-opps-viewer__body__info-item__value">
                {formatDay(dayFormatType.UKWITHHM, createdAt)}
              </span>
            </div>
          </Col>
          <Col span={12}>
            <div className="payout-opps-viewer__body__info-item">
              <span className="payout-opps-viewer__body__info-item__label">{`${t(
                'cms.referral.payout.opps.modal.assigned_bc.label',
              )}: `}</span>
              <span className="payout-opps-viewer__body__info-item__value">{contactName}</span>
            </div>
          </Col>
          <Col span={12}>
            <div className="payout-opps-viewer__body__info-item">
              <span className="payout-opps-viewer__body__info-item__label">
                {`${t('cms.referral.payout.opps.modal.updated_time.label')}: `}
              </span>
              <span className="payout-opps-viewer__body__info-item__value">
                {formatDay(dayFormatType.UKWITHHM, updatedAt)}
              </span>
            </div>
          </Col>
        </Row>
      </section>

      <section className="payout-opps-viewer__section">
        <h3 className="payout-opps-viewer__body__title">
          {t('cms.referral.payout.opps.modal.payout_info.title')}
        </h3>
        <Row gutter={24}>
          <Col span={12}>
            <div className="payout-opps-viewer__body__info-item">
              <span className="payout-opps-viewer__body__info-item__label">
                {`${t(
                  `cms.referral.payout.opps.modal.total_comission_from_this_opps.label.${
                    bookingJourney === bookingJourneyType.MANUAL ? 'opps' : 'order'
                  }`,
                )}: `}
              </span>
              <span className="payout-opps-viewer__body__info-item__value">
                {`${formatPriceWithSpace(
                  partnerActualCommission?.amount,
                  partnerActualCommission?.currency,
                )}`}
              </span>
            </div>

            <div className="payout-opps-viewer__body__info-item payout-opps-viewer__body__info-item--center">
              <span className="payout-opps-viewer__body__info-item__label">
                {`${t('cms.referral.payout.opps.modal.first_payout.label')}: `}
                {` `}
              </span>
              <span className="payout-opps-viewer__body__info-item__value">
                {`${formatPriceWithSpace(result?.amount, result?.currency)}`}
              </span>
              <If condition={selectedPayout.id === result.id}>
                <Tooltip
                  overlayClassName="payout-opps-viewer__body__info-item__tooltip"
                  trigger="hover"
                  title={t('cms.referral.payout.opps.modal.payout.tooltip')}
                >
                  <PayoutOppsInfoIcon
                    style={{ marginTop: '-2' }}
                    className="payout-opps-viewer__body__info-item__after-icon"
                  />
                </Tooltip>
              </If>
            </div>

            <div className="payout-opps-viewer__body__info-item">
              <span className="payout-opps-viewer__body__info-item__label">
                {`${t('cms.referral.payout.opps.modal.payout_plan.label')}: `}
              </span>
              <span className="payout-opps-viewer__body__info-item__value">
                {`${
                  PayoutPlanStatus.Locked.includes(partnerActualCommission?.status)
                    ? 'Locked'
                    : 'Unlocked'
                }`}
              </span>
            </div>
          </Col>

          <Col span={12}>
            <div className="payout-opps-viewer__body__info-item">
              <span className="payout-opps-viewer__body__info-item__label">
                {`${t('cms.referral.payout.opps.modal.tenancy_type.label')}: `}
              </span>
              <span className="payout-opps-viewer__body__info-item__value">
                {`${Object.keys(LeaseType)[Object.values(LeaseType).indexOf(result.leaseType)]}`}
              </span>
            </div>

            <div className="payout-opps-viewer__body__info-item payout-opps-viewer__body__info-item--center">
              <span className="payout-opps-viewer__body__info-item__label">
                {`${t('cms.referral.payout.opps.modal.second_payout.label')}: `}
              </span>
              <span className="payout-opps-viewer__body__info-item__value">
                {`${
                  secondOpps ? formatPriceWithSpace(secondOpps.amount, secondOpps.currency) : '-'
                }`}
              </span>
              <If condition={selectedPayout.id === secondOpps.id}>
                <Tooltip
                  trigger="hover"
                  overlayClassName="payout-opps-viewer__body__info-item__tooltip"
                  title={t('cms.referral.payout.opps.modal.payout.tooltip')}
                >
                  <PayoutOppsInfoIcon
                    style={{ marginTop: '-2' }}
                    className="payout-opps-viewer__body__info-item__after-icon"
                  />
                </Tooltip>
              </If>
            </div>
          </Col>
        </Row>
      </section>

      <div className="payout-opps-viewer__footer">
        <Button
          type="primary"
          size="large"
          disabled={auditPayoutLoading}
          className={classNames('payout-opps-viewer__save-button', {
            'payout-opps-viewer__save-button--loading': auditPayoutLoading,
          })}
          onClick={async () => {
            if (isAvaliableAuditPayoutStatus) {
              await handleAudit();
            } else {
              onClose();
            }
          }}
        >
          <If condition={auditPayoutLoading}>
            <Spin
              indicator={
                <Icon
                  className="payout-opps-viewer__button__icon"
                  type="loading"
                  style={{ fontSize: 14, color: 'white' }}
                  spin
                />
              }
            />
          </If>
          <Choose>
            <When condition={payoutStatus === Statuses.Pending}>
              {t('cms.referral.payout.opps.modal.btn.add')}
            </When>
            <When condition={payoutStatus === Statuses.Approved}>
              {t('cms.referral.payout.opps.modal.btn.remove')}
            </When>
            <Otherwise>{t('cms.referral.payout.opps.modal.btn.close')}</Otherwise>
          </Choose>
        </Button>
      </div>
    </>
  );
}

export default PayoutOppsViewer;
